import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Field';
import { Banner, EmptyState, ErrorState, Skeleton } from '@/components/ui/Feedback';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { cn } from '@/lib/cn';
import { useAuth } from '@/features/auth/AuthContext';
import { portfolioApi, type PortfolioRow } from './portfolioApi';
import type { PortfolioCategory } from '@lentera/shared';

const CATEGORIES: { key: PortfolioCategory; label: string; desc: string; icon: string }[] = [
  {
    key: 'prestasi',
    label: 'Prestasi',
    desc: 'Lomba, kompetisi, penghargaan, atau amanah kepengurusan organisasi',
    icon: '🏆',
  },
  {
    key: 'karya',
    label: 'Karya',
    desc: 'Buku, lagu, puisi, esai, modul, atau karya cipta literasi lainnya',
    icon: '🎨',
  },
  {
    key: 'publikasi',
    label: 'Publikasi',
    desc: 'Tautan konten media sosial (IG, TikTok, YouTube) atau media tulisan/berita',
    icon: '📱',
  },
];

interface FormState {
  category: PortfolioCategory;
  title: string;
  description: string;
  dateOrYear: string;
  linkUrl: string;
}

const EMPTY_FORM: FormState = {
  category: 'prestasi',
  title: '',
  description: '',
  dateOrYear: '',
  linkUrl: '',
};

export function PelitaPortfolioTab({ individualId }: { individualId: string }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeCat, setActiveCat] = useState<PortfolioCategory | 'all'>('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<PortfolioRow | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [deletingItem, setDeletingItem] = useState<PortfolioRow | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const canManage =
    user?.role === 'super_admin' ||
    (user?.role === 'pelita' && user?.individualId === individualId);

  const { data: rows, isLoading, error, refetch } = useQuery({
    queryKey: ['portfolios', individualId],
    queryFn: () => portfolioApi.list({ individualId }),
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (editingItem) {
        return portfolioApi.update(editingItem.id, {
          category: form.category,
          title: form.title,
          description: form.description || null,
          dateOrYear: form.dateOrYear || null,
          linkUrl: form.linkUrl || null,
        });
      } else {
        return portfolioApi.create({
          individualId,
          category: form.category,
          title: form.title,
          description: form.description || null,
          dateOrYear: form.dateOrYear || null,
          linkUrl: form.linkUrl || null,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portfolios', individualId] });
      setModalOpen(false);
      setEditingItem(null);
      setForm(EMPTY_FORM);
      setErrorMsg(null);
    },
    onError: (err: Error) => {
      setErrorMsg(err.message || 'Gagal menyimpan portofolio.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => portfolioApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portfolios', individualId] });
      setDeletingItem(null);
    },
  });

  const handleOpenAdd = (defaultCat?: PortfolioCategory) => {
    setEditingItem(null);
    setForm({
      ...EMPTY_FORM,
      category: defaultCat || (activeCat === 'all' ? 'prestasi' : activeCat),
    });
    setErrorMsg(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (item: PortfolioRow) => {
    setEditingItem(item);
    setForm({
      category: item.category,
      title: item.title,
      description: item.description || '',
      dateOrYear: item.dateOrYear || '',
      linkUrl: item.linkUrl || '',
    });
    setErrorMsg(null);
    setModalOpen(true);
  };

  if (isLoading) return <Skeleton className="h-48 w-full" />;
  if (error) return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;

  const items = rows ?? [];
  const filteredItems = activeCat === 'all' ? items : items.filter((i) => i.category === activeCat);

  return (
    <div className="flex flex-col gap-5">
      {/* Category Pills & Action */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setActiveCat('all')}
            className={cn(
              'rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all cursor-pointer border',
              activeCat === 'all'
                ? 'bg-primary text-white border-primary shadow-xs'
                : 'bg-white border-border text-ink-secondary hover:text-ink hover:border-ink-muted',
            )}
          >
            Semua ({items.length})
          </button>
          {CATEGORIES.map((cat) => {
            const count = items.filter((i) => i.category === cat.key).length;
            const isSelected = activeCat === cat.key;
            return (
              <button
                key={cat.key}
                type="button"
                onClick={() => setActiveCat(cat.key)}
                className={cn(
                  'rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all cursor-pointer border flex items-center gap-1.5',
                  isSelected
                    ? 'bg-primary text-white border-primary shadow-xs'
                    : 'bg-white border-border text-ink-secondary hover:text-ink hover:border-ink-muted',
                )}
              >
                <span>{cat.icon}</span>
                <span>{cat.label}</span>
                <span className={cn('text-[11px] rounded-full px-1.5 py-0.2', isSelected ? 'bg-white/20' : 'bg-subtle text-ink-secondary')}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {canManage && (
          <Button onClick={() => handleOpenAdd()}>
            + Tambah Portofolio
          </Button>
        )}
      </div>

      {/* Overview Cards by Category */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {CATEGORIES.map((cat) => (
          <div
            key={cat.key}
            onClick={() => setActiveCat(cat.key)}
            className={cn(
              'rounded-lg border p-3.5 transition-all cursor-pointer',
              activeCat === cat.key
                ? 'border-primary bg-primary-soft/30 shadow-xs'
                : 'border-border bg-white hover:border-primary/50',
            )}
          >
            <div className="flex items-center justify-between">
              <span className="text-xl">{cat.icon}</span>
              <span className="text-xs font-bold px-2 py-0.5 rounded bg-subtle text-ink">
                {items.filter((i) => i.category === cat.key).length} item
              </span>
            </div>
            <h4 className="mt-2 text-sm font-bold text-ink">{cat.label}</h4>
            <p className="mt-0.5 text-xs text-ink-secondary line-clamp-2">{cat.desc}</p>
          </div>
        ))}
      </div>

      {/* Items List */}
      {filteredItems.length === 0 ? (
        <Card padded={false}>
          <EmptyState
            icon="📁"
            title="Belum ada portofolio di kategori ini"
            description={
              canManage
                ? 'Klik "+ Tambah Portofolio" untuk menambahkan prestasi, karya, atau publikasi Anda.'
                : 'Belum ada rekam jejak portofolio yang diunggah untuk kategori ini.'
            }
          />
        </Card>
      ) : (
        <div className="grid gap-3">
          {filteredItems.map((item) => {
            const catMeta = CATEGORIES.find((c) => c.key === item.category);
            return (
              <Card key={item.id} className="p-4 sm:p-5">
                <div className="flex flex-col sm:flex-row items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-primary-soft text-primary">
                        {catMeta?.icon} {catMeta?.label}
                      </span>
                      {item.dateOrYear && (
                        <span className="text-xs font-medium text-ink-muted tabular">
                          · {item.dateOrYear}
                        </span>
                      )}
                    </div>
                    <h3 className="text-base font-bold text-ink">{item.title}</h3>
                    {item.description && (
                      <p className="mt-1 text-sm text-ink-secondary leading-relaxed whitespace-pre-line">
                        {item.description}
                      </p>
                    )}
                    {item.linkUrl && (
                      <div className="mt-2.5">
                        <a
                          href={item.linkUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline bg-primary/5 px-2.5 py-1 rounded border border-primary/20"
                        >
                          <span>🔗</span>
                          <span>Buka Tautan Portofolio / Konten</span>
                        </a>
                      </div>
                    )}
                  </div>

                  {canManage && (
                    <div className="flex items-center gap-2 shrink-0 self-end sm:self-start">
                      <Button size="sm" variant="secondary" onClick={() => handleOpenEdit(item)}>
                        Ubah
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setDeletingItem(item)}
                        className="text-danger hover:bg-danger-soft"
                      >
                        Hapus
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add / Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-xs">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold text-ink mb-1">
              {editingItem ? 'Ubah Portofolio' : 'Tambah Portofolio Baru'}
            </h3>
            <p className="text-xs text-ink-secondary mb-4">
              Rekam jejak ini akan otomatis terangkum dalam CV PDF Anda.
            </p>

            {errorMsg && (
              <div className="mb-4">
                <Banner tone="danger">{errorMsg}</Banner>
              </div>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                saveMutation.mutate();
              }}
              className="space-y-4 text-left"
            >
              <Select
                label="Kategori Portofolio"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value as PortfolioCategory })}
              >
                <option value="prestasi">Prestasi (Lomba / Kompetisi / Organisasi)</option>
                <option value="karya">Karya (Buku / Lagu / Puisi / dll)</option>
                <option value="publikasi">Publikasi (Konten Media Sosial / Media Berita / Web)</option>
              </Select>

              <Input
                label="Judul Portofolio *"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Contoh: Juara 1 Festival Literasi Nasional 2025"
                required
              />

              <Input
                label="Tahun atau Tanggal Pelaksanaan"
                value={form.dateOrYear}
                onChange={(e) => setForm({ ...form, dateOrYear: e.target.value })}
                placeholder="Contoh: 2025 atau Agustus 2024"
              />

              <div>
                <label className="block text-xs font-medium text-ink-secondary mb-1">
                  Deskripsi / Keterangan Singkat
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  className="w-full rounded-[var(--radius-input)] border border-border bg-white px-3 py-2 text-sm text-ink placeholder:text-ink-muted focus:border-primary focus:outline-none"
                  placeholder="Ringkasan peran, capaian, atau deskripsi karya literasi..."
                />
              </div>

              <Input
                label="Tautan URL / Link Bukti / Media (Opsional)"
                type="url"
                value={form.linkUrl}
                onChange={(e) => setForm({ ...form, linkUrl: e.target.value })}
                placeholder="https://instagram.com/p/... atau https://youtube.com/..."
                hint="Bisa berupa link postingan Instagram, video YouTube/TikTok, atau tautan artikel."
              />

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-border">
                <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>
                  Batal
                </Button>
                <Button type="submit" loading={saveMutation.isPending} disabled={!form.title.trim()}>
                  {editingItem ? 'Simpan Perubahan' : 'Tambahkan'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deletingItem !== null}
        title="Hapus Entri Portofolio?"
        confirmLabel="Hapus"
        loading={deleteMutation.isPending}
        onConfirm={() => deletingItem && deleteMutation.mutate(deletingItem.id)}
        onCancel={() => setDeletingItem(null)}
      >
        {deletingItem && (
          <p className="text-sm text-ink-secondary">
            Apakah Anda yakin ingin menghapus portofolio{' '}
            <strong className="text-ink">"{deletingItem.title}"</strong>? Tindakan ini tidak dapat dibatalkan.
          </p>
        )}
      </ConfirmDialog>
    </div>
  );
}
